import { useEffect } from "react";
import {
  Box,
  Button,
  Fade,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { CaseViewers } from "../../components/cases/CaseViewers";
import { ContentTitleBar } from "../../components/ContentTitleBar";
import { useNavigate } from "react-router-dom";
import { useCase } from "../../store/case/hooks";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const AddCaseSchema = z.object({
  caseName: z.string().trim().nonempty({ message: "A case name is required." }),
  caseFileNumber: z.string().trim().optional(),
  fieldOffice: z.string().trim().optional(),
  description: z.string().trim().optional(),
  viewAll: z.boolean().optional(),
  viewersList: z
    .array(
      z.object({
        key: z.number(),
        label: z.string(),
        avatar: z.string(),
      }),
    )
    .optional(),
});

export default function AddCasePage() {
  const caseHooks = useCase();
  const navigate = useNavigate();
  const { createCase } = caseHooks;
  const af_case = caseHooks.case;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(AddCaseSchema),
    defaultValues: {
      description: "",
      caseName: "",
      caseFileNumber: "",
      fieldOffice: "",
      viewAll: false,
      viewersList: [],
    },
    mode: "all",
  });

  const viewAll = watch("viewAll");

  const onSubmit = (data) => {
    createCase(data);
    reset();
  };

  const handleCancel = () => {
    reset();
    navigate("/app/cases");
  };

  useEffect(() => {
    if (!af_case.creating && af_case.id !== undefined) {
      navigate(`/app/cases/${af_case.id}/addClip`, { replace: true });
    }
  }, [af_case, navigate]);

  return (
    <Stack content="main" height={1}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack flex={1} p={4}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5">Case Details</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="caseName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    error={!!errors.caseName}
                    fullWidth
                    helperText={
                      errors.caseName
                        ? errors.caseName?.message
                        : "Please enter a name for the case."
                    }
                    label="Case Name"
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Controller
                name="caseFileNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    fullWidth
                    label="Case Number"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Controller
                name="fieldOffice"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    fullWidth
                    label="Field Office"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    fullWidth
                    label="Case Description"
                  />
                )}
              />
            </Grid>
            <Grid mt={3} size={{ xs: 12 }}>
              <Typography variant="h5">Team (Optional)</Typography>
            </Grid>
            <Grid display="flex" alignItems="center" size={{ xs: 12 }}>
              <Controller
                name="viewAll"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        slotProps={{
                          input: { "aria-label": "all-users" },
                        }}
                      />
                    }
                    label="All Users"
                  />
                )}
              />
            </Grid>
            <Fade in={!viewAll} sx={{ width: 1 }}>
              <Grid size={{ xs: 12 }} maxWidth={600}>
                <CaseViewers control={control} name="viewersList" />
              </Grid>
            </Fade>
          </Grid>
        </Stack>
        <Paper
          elevation={4}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1,
            position: "fixed",
            transform: "translateX(-50%)",
            bottom: 72,
            left: "50%",
            zIndex: 1,
            width: "calc(100% - 1rem)",
          }}
        >
          <Button variant="outlined" onClick={handleCancel}>
            CANCEL
          </Button>
          <Button disabled={!isValid} variant="contained" type="submit">
            CREATE FIRST CLIP
          </Button>
        </Paper>
        <Box position="fixed" bottom={0} width={1} zIndex={1}>
          <ContentTitleBar
            label="Start a new case"
            onBack={() => navigate("/app/cases/")}
          />
        </Box>
      </Box>
    </Stack>
  );
}
